import { t } from '@lingui/core/macro'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import type { DateSearchValue } from '../utils/dateSearchValue'
import { parseDateInput } from '../utils/parseDateInput'
import { DateInputShell } from './DateInputShell'

export type { DateSearchValue } from '../utils/dateSearchValue'

export type DateSearchInputProps = {
  value: DateSearchValue | null | undefined
  onChange: (value: DateSearchValue | null) => void
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  status?: 'error' | 'warning'
  size?: 'small' | 'middle' | 'large'
  id?: string
  className?: string
}

function formatRangeToDisplay(value: DateSearchValue | null | undefined): string {
  if (!value) return ''
  const from = dayjs(value.from, 'YYYY-MM-DD', true)
  const to = dayjs(value.to, 'YYYY-MM-DD', true)
  if (!from.isValid() || !to.isValid()) return ''

  if (from.isSame(to, 'day')) return from.format('DD.MM.YYYY')

  if (from.year() === to.year() && from.month() === to.month()) {
    const lastDay = from.endOf('month').date()
    if (from.date() === 1 && to.date() === lastDay) return from.format('M.YYYY')
  }

  if (from.year() === to.year()) {
    if (from.month() === 0 && from.date() === 1 && to.month() === 11 && to.date() === 31) {
      return from.format('YYYY')
    }
  }

  return `${from.format('DD.MM.YYYY')} - ${to.format('DD.MM.YYYY')}`
}

export function DateSearchInput(props: DateSearchInputProps) {
  const { value, onChange, ...rest } = props
  const [text, setText] = useState(() => formatRangeToDisplay(value))
  const [error, setError] = useState(false)

  useEffect(() => {
    setText(formatRangeToDisplay(value))
    setError(false)
  }, [value])

  const commit = () => {
    const parsed = parseDateInput(text, { allowPartial: true })
    if (!parsed.ok && parsed.reason === 'empty') {
      setError(false)
      onChange(null)
      return
    }
    if (!parsed.ok) {
      setError(true)
      return
    }
    setError(false)
    const next: DateSearchValue = { from: parsed.value.from, to: parsed.value.to }
    setText(formatRangeToDisplay(next))
    onChange(next)
  }

  const revert = () => {
    setText(formatRangeToDisplay(value))
    setError(false)
  }

  return (
    <DateInputShell
      placeholder={t`z. B. 1951, 06.1951 oder 15.06.1951`}
      {...rest}
      text={text}
      onTextChange={next => {
        setText(next)
        if (error) setError(false)
      }}
      onCommit={commit}
      onRevert={revert}
      error={error}
      popoverPicker="year"
      popoverValue={value?.from ? dayjs(value.from, 'YYYY-MM-DD', true) : null}
      onPopoverChange={d => {
        if (!d) {
          setError(false)
          onChange(null)
          return
        }
        const next: DateSearchValue = {
          from: d.startOf('year').format('YYYY-MM-DD'),
          to: d.endOf('year').format('YYYY-MM-DD'),
        }
        setError(false)
        setText(formatRangeToDisplay(next))
        onChange(next)
      }}
    />
  )
}
